import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro' // As per i18n guidelines
import styled from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { breakpoints } from '@/config/layout'

import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import type { FC } from 'react'

/**
 * Represents a single tab item in the MobileNavigation.
 */
export type MobileNavigationItem = {
  id: string
  label: string // This will be a translation key
  icon: IconProp
  iconActive?: IconProp
  isActive?: boolean
  badgeCount?: number
  hasNotification?: boolean
  onClick: (id: string) => void
  href?: string // Optional href for navigation
}

/**
 * BottomTabBarProps defines the properties for the BottomTabBar component.
 */
export type MobileNavigationProps = {
  /**
   * An array of tab items to display in the navigation bar.
   */
  items: MobileNavigationItem[]
}

/**
 * MobileNavigation provides a responsive bottom navigation bar.
 * It displays a series of tabs, each with an icon and a label.
 * Supports active states and notification badges.
 */
export const MobileNavigation: FC<MobileNavigationProps> = ({ items = [] }) => {
  if (!items || items.length === 0) {
    return null // Don't render anything if there are no tabs
  }

  return (
    <StyledBottomTabBarContainer role='navigation' aria-label={t`bottom.navigation.aria.label`}>
      {items.map((tab) => (
        <StyledTabItem
          key={tab.id}
          isActive={tab.isActive}
          onClick={() => tab.onClick(tab.id)}
          aria-current={tab.isActive ? 'page' : undefined}
          aria-label={t({ id: tab.label, message: tab.label })} // Assuming label is a key, provide default
        >
          <StyledIconContainer>
            <FontAwesomeIcon icon={tab.isActive ? (tab.iconActive ?? tab.icon) : tab.icon} />
            {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
              <StyledBadge aria-label={t`notification.count ${tab.badgeCount}`}>
                {tab.badgeCount > 99 ? '99' : tab.badgeCount}
              </StyledBadge>
            )}
            {tab.hasNotification && <StyledNotificationDot />}
          </StyledIconContainer>
          <StyledLabel isActive={tab.isActive}>{t({ id: tab.label, message: tab.label })}</StyledLabel>
        </StyledTabItem>
      ))}
    </StyledBottomTabBarContainer>
  )
}

const StyledBottomTabBarContainer = styled.nav`
  display: flex;
  width: 100%;
  height: 84px;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space3}
    ${({ theme }) => theme.UI.SpacingPx.Space2};

  justify-content: space-around;
  align-items: flex-start;
  flex-shrink: 0;
  background: ${({ theme }) => theme.Colors.Background.White};
  box-shadow: 0px -1px 20px 0px rgba(23, 39, 57, 0.04);

  @media ${breakpoints.m} {
    max-width: 768px; // Example: constrain width on tablets/desktops if it's a mobile-first bottom bar
    margin: 0 auto;
  }
`

export const StyledTabItem = styled.button<{ isActive?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 ${({ theme }) => theme.UI.SpacingPx.Space1};
  gap: 4px;
  position: relative;
  flex: 1;
  min-width: 0;
  color: ${({ theme, isActive }) =>
    isActive ? (theme.Colors.Theme.Primary[700] ?? '#022248') : theme.Components.Navigation.Text};
  font-weight: ${({ isActive }) => (isActive ? 'bold' : 'normal')};

  &:hover {
    color: ${({ theme }) => theme.Colors.Theme.Primary[700] ?? '#022248'};
  }
`

export const StyledIconContainer = styled.div`
  width: 24px; // Standard icon size
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  svg {
    width: 100%;
    height: 100%;
  }
`

export const StyledLabel = styled(Span).attrs({
  type: 'Label',
})<{ type?: string; isActive?: boolean }>`
  font-size: 12px;
  line-height: 16px;
  text-transform: initial;
  letter-spacing: 0;
  text-align: center;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  min-width: 0;
  font-weight: ${({ isActive }) => (isActive ? 'bold' : 'normal')};
`

export const StyledBadge = styled.span`
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: ${({ theme }) => theme.Colors.Status.Error};
  color: ${({ theme }) => theme.Colors.Background.White};
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  min-width: 20px;
  height: 20px;
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${({ theme }) => theme.Colors.Neutrals.Lighter};
`

export const StyledNotificationDot = styled.div`
  position: absolute;
  top: 0px;
  right: -4px;
  width: 8px;
  height: 8px;
  background-color: ${({ theme }) => theme.Colors.Status.Error};
  border-radius: 50%;
`
